export const SAVINGS_CURRENCIES = [
    { value: "ARS", label: "ARS · Peso argentino" },
    { value: "USD", label: "USD · Dólar estadounidense" },
    { value: "EUR", label: "EUR · Euro" },
    { value: "BRL", label: "BRL · Real brasileño" },
    { value: "UYU", label: "UYU · Peso uruguayo" },
    { value: "CLP", label: "CLP · Peso chileno" },
    { value: "GBP", label: "GBP · Libra esterlina" },
];

export const SAVINGS_ACCOUNT_TYPES = [
    { value: "BANK", label: "Cuenta bancaria" },
    { value: "WALLET", label: "Billetera virtual" },
    { value: "CASH", label: "Efectivo" },
    { value: "INVESTMENT", label: "Inversión / fondo" },
    { value: "BROKER", label: "Broker / ALyC" },
    { value: "TERM_DEPOSIT", label: "Plazo fijo" },
    { value: "CRYPTO", label: "Cripto / exchange" },
    { value: "CREDIT_CARD", label: "Tarjeta / deuda" },
    { value: "OTHER", label: "Otro" },
];

export const SAVINGS_TRANSACTION_TYPES = [
    { value: "INCOME", label: "Ingreso" },
    { value: "EXPENSE", label: "Gasto" },
    { value: "TRANSFER", label: "Transferencia entre cuentas" },
    { value: "YIELD", label: "Rendimiento / interés" },
    { value: "ADJUSTMENT", label: "Ajuste manual" },
];

export const SAVINGS_CATEGORIES = [
    "Sueldo", "Freelance", "Ventas", "Comisiones", "Dividendos", "Intereses", "Cashback", "Otros ingresos",
    "Vivienda", "Alquiler", "Expensas", "Supermercado", "Comida", "Delivery", "Transporte", "Combustible",
    "Salud", "Farmacia", "Educación", "Cursos", "Servicios", "Internet", "Telefonía", "Electricidad", "Gas", "Agua",
    "Suscripciones", "Software", "Entretenimiento", "Salidas", "Ropa", "Compras", "Impuestos", "Seguros", "Familia",
    "Mascotas", "Viajes", "Regalos", "Deudas", "Ahorro / inversión", "Comisiones bancarias", "Otro",
].map((value) => ({ value, label: value }));

export const SAVINGS_MONTHS = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
].map((label, index) => ({ value: String(index + 1), label }));

export const SAVINGS_GOAL_PRIORITIES = [
    { value: "LOW", label: "Baja" }, { value: "MEDIUM", label: "Media" }, { value: "HIGH", label: "Alta" },
];

export const SAVINGS_GOAL_STATUS = [
    { value: "ACTIVE", label: "Activa" }, { value: "PAUSED", label: "Pausada" }, { value: "COMPLETED", label: "Completada" },
];

export const SAVINGS_RECURRENCES = [
    { value: "WEEKLY", label: "Semanal" }, { value: "MONTHLY", label: "Mensual" },
    { value: "QUARTERLY", label: "Trimestral" }, { value: "YEARLY", label: "Anual" },
];

export const onSavingsMoney = (value, currency = "ARS") => {
    try {
        return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(value || 0));
    } catch {
        return `${currency} ${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;
    }
};

export const onSavingsDate = (value) => value
    ? new Date(value).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })
    : "-";

export const onSavingsOptionLabel = (options, value, fallback = value || "-") => options.find((option) => option.value === value)?.label || fallback;

export const onSavingsAccountDelta = (transaction, accountId) => {
    if (!transaction || transaction.status !== "CLEARED") return 0;
    const amount = Number(transaction.amount || 0);
    if (transaction.type === "TRANSFER") {
        if (transaction.accountId === accountId) return -amount;
        if (transaction.targetAccountId === accountId) return Number(transaction.targetAmount ?? transaction.amount ?? 0);
        return 0;
    }
    if (transaction.accountId !== accountId) return 0;
    if (transaction.type === "EXPENSE") return -amount;
    if (transaction.type === "ADJUSTMENT") return transaction.direction === "DEBIT" ? -amount : amount;
    return amount;
};

export const onSavingsAccountBalance = (account, transactions = []) => Number(account?.openingBalance || 0)
    + transactions.reduce((sum, transaction) => sum + onSavingsAccountDelta(transaction, account?.id), 0);

export const onSavingsGoalCurrent = (goal) => (goal?.movements || []).reduce((sum, movement) => (
    sum + (movement.type === "WITHDRAWAL" ? -Number(movement.amount || 0) : Number(movement.amount || 0))
), 0);

export const onSavingsAnalytics = ({ accounts = [], transactions = [], goals = [], budgets = [], currency = "ARS", year, month }) => {
    const selectedYear = Number(year || new Date().getFullYear());
    const selectedMonth = Number(month || new Date().getMonth() + 1);
    const currencyAccounts = accounts.filter((account) => account.currency === currency && account.isActive !== false);
    const accountIds = new Set(currencyAccounts.map((account) => account.id));

    const belongsToCurrency = (transaction) => accountIds.has(transaction.accountId) || accountIds.has(transaction.targetAccountId);
    const cleared = transactions.filter((transaction) => transaction.status === "CLEARED" && belongsToCurrency(transaction));
    const inPeriod = (transaction, targetYear = selectedYear, targetMonth = null) => {
        const date = new Date(transaction.occurredAt);
        return date.getFullYear() === targetYear && (targetMonth === null || date.getMonth() + 1 === targetMonth);
    };
    const flow = (rows) => rows.reduce((acc, transaction) => {
        const amount = Number(transaction.amount || 0);
        if (transaction.type === "INCOME") acc.income += amount;
        if (transaction.type === "EXPENSE") acc.expense += amount;
        if (transaction.type === "YIELD") acc.yield += amount;
        return acc;
    }, { income: 0, expense: 0, yield: 0 });

    const monthRows = cleared.filter((row) => inPeriod(row, selectedYear, selectedMonth));
    const yearRows = cleared.filter((row) => inPeriod(row, selectedYear));
    const pendingMonthRows = transactions
        .filter((transaction) => transaction.status === "PENDING" && belongsToCurrency(transaction))
        .filter((row) => inPeriod(row, selectedYear, selectedMonth));
    const monthFlow = flow(monthRows);
    const pendingMonthFlow = flow(pendingMonthRows);
    const yearFlow = flow(yearRows);
    const totalBalance = currencyAccounts.reduce((sum, account) => sum + onSavingsAccountBalance(account, transactions), 0);

    const monthly = SAVINGS_MONTHS.map((entry, index) => {
        const values = flow(cleared.filter((row) => inPeriod(row, selectedYear, index + 1)));
        return { month: entry.label.slice(0, 3), ...values, net: values.income + values.yield - values.expense };
    });

    const years = Array.from({ length: 5 }, (_, index) => selectedYear - 4 + index);
    const yearly = years.map((targetYear) => {
        const values = flow(cleared.filter((row) => inPeriod(row, targetYear)));
        return { year: String(targetYear), ...values, net: values.income + values.yield - values.expense };
    });

    const categoryMap = new Map();
    monthRows.filter((row) => row.type === "EXPENSE").forEach((row) => {
        const key = row.category || "Sin categoría";
        categoryMap.set(key, (categoryMap.get(key) || 0) + Number(row.amount || 0));
    });
    const categories = [...categoryMap.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

    const accountDistribution = currencyAccounts.map((account) => ({
        name: account.name,
        value: onSavingsAccountBalance(account, transactions),
        account,
    })).sort((a, b) => b.value - a.value);

    const activeGoals = goals.filter((goal) => goal.currency === currency && goal.status !== "COMPLETED");
    const goalTarget = activeGoals.reduce((sum, goal) => sum + Number(goal.targetAmount || 0), 0);
    const goalSaved = activeGoals.reduce((sum, goal) => sum + onSavingsGoalCurrent(goal), 0);

    const budgetRows = budgets.filter((budget) => budget.currency === currency && Number(budget.year) === selectedYear && Number(budget.month) === selectedMonth)
        .map((budget) => {
            const spent = monthRows.filter((row) => row.type === "EXPENSE" && (row.category || "Sin categoría") === budget.category)
                .reduce((sum, row) => sum + Number(row.amount || 0), 0);
            const pending = pendingMonthRows.filter((row) => row.type === "EXPENSE" && (row.category || "Sin categoría") === budget.category)
                .reduce((sum, row) => sum + Number(row.amount || 0), 0);
            const limit = Number(budget.limitAmount || 0);
            return {
                ...budget,
                spent,
                pending,
                projectedSpent: spent + pending,
                remaining: Math.max(0, limit - spent),
                projectedRemaining: Math.max(0, limit - spent - pending),
                percent: limit > 0 ? (spent / limit) * 100 : 0,
                projectedPercent: limit > 0 ? ((spent + pending) / limit) * 100 : 0,
            };
        });

    const budgetTotal = budgetRows.reduce((sum, budget) => sum + Number(budget.limitAmount || 0), 0);
    const budgetedCategories = new Set(budgetRows.map((budget) => budget.category));
    const unbudgetedExpense = monthRows
        .filter((row) => row.type === "EXPENSE" && !budgetedCategories.has(row.category || "Sin categoría"))
        .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const incomeRows = transactions
        .filter((row) => (row.status === "CLEARED" || row.status === "PENDING") && row.type === "INCOME" && accountIds.has(row.accountId))
        .filter((row) => inPeriod(row, selectedYear, selectedMonth))
        .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
    const incomeConfirmed = monthFlow.income;
    const incomePending = pendingMonthFlow.income;
    const expenseConfirmed = monthFlow.expense;
    const expensePending = pendingMonthFlow.expense;
    const projectedIncome = incomeConfirmed + incomePending;
    const projectedExpense = expenseConfirmed + expensePending;
    const budgetCoverage = incomeConfirmed > 0 ? (budgetTotal / incomeConfirmed) * 100 : 0;
    const projectedBudgetCoverage = projectedIncome > 0 ? (budgetTotal / projectedIncome) * 100 : 0;

    const inflowMonth = monthFlow.income + monthFlow.yield;
    return {
        totalBalance,
        month: { ...monthFlow, net: monthFlow.income + monthFlow.yield - monthFlow.expense, savingsRate: inflowMonth > 0 ? ((inflowMonth - monthFlow.expense) / inflowMonth) * 100 : 0 },
        year: { ...yearFlow, net: yearFlow.income + yearFlow.yield - yearFlow.expense },
        monthly, yearly, categories, accountDistribution, budgetRows,
        budgetOverview: {
            incomeConfirmed,
            incomePending,
            projectedIncome,
            expenseConfirmed,
            expensePending,
            projectedExpense,
            budgetTotal,
            budgetCoverage,
            projectedBudgetCoverage,
            availableToBudget: incomeConfirmed - budgetTotal,
            projectedAvailableToBudget: projectedIncome - budgetTotal,
            cashRemainder: incomeConfirmed - expenseConfirmed,
            projectedCashRemainder: projectedIncome - projectedExpense,
            unbudgetedExpense,
            yieldConfirmed: monthFlow.yield,
            yieldPending: pendingMonthFlow.yield,
            incomeRows,
        },
        goals: { target: goalTarget, saved: goalSaved, progress: goalTarget > 0 ? (goalSaved / goalTarget) * 100 : 0 },
        pendingExpenses: transactions.filter((row) => row.status === "PENDING" && row.type === "EXPENSE" && accountIds.has(row.accountId)).reduce((sum, row) => sum + Number(row.amount || 0), 0),
    };
};
